import type { DataIdentifier, DocumentViewerCardContentEncryptedKeyValueData, PiiJsonValue } from '@onefootprint/request-types/dashboard';
import { LinkButton, LoadingSpinner } from '@onefootprint/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldOrPlaceholder from '@/components/field-or-placeholder';
import { useDecryptedVaultDataStore } from '../../hooks';
type DecryptedData = Record<string, PiiJsonValue>;

// Stable empty object to avoid creating new references in selectors
const EMPTY_DECRYPTED_DATA: DecryptedData = {};
type EncryptedKeyValueContentProps = {
  content: DocumentViewerCardContentEncryptedKeyValueData;
  onDecrypt?: (fields: DataIdentifier[]) => Promise<DecryptedData | null>;
  documentIdentifier: string;
};
const EncryptedKeyValueContent = ({
  content,
  onDecrypt,
  documentIdentifier
}: EncryptedKeyValueContentProps) => {
  const {
    t
  } = useTranslation('document-viewer');
  const [isDecrypting, setIsDecrypting] = useState(false);
  // Use Zustand store to persist decrypted data across tabs and cards, scoped by document identifier
  // Selector accesses the specific document's data reactively
  const decryptedData = useDecryptedVaultDataStore(state => state.decryptedDataByDocument[documentIdentifier] ?? EMPTY_DECRYPTED_DATA);
  const addDecryptedData = useDecryptedVaultDataStore(state_0 => state_0.addDecryptedData);
  if (content.data.length === 0) {
    return <p className="text-body-2 text-tertiary">{t($ => $.encryptedKeyValue.noData)}</p>;
  }

  // Get the display value for a field:
  // 1. Check decrypted vault data first (values decrypted this session, persisted in Zustand store)
  // 2. Check field.kind: 'decrypted' → use field.value
  // 3. Check field.kind: 'encrypted_vault' with dataIdentifier → return null (shows encrypted bullets)
  // 4. Check field.kind: 'encrypted_vault' without dataIdentifier → return undefined (shows "-")
  const getFieldDisplayValue = (field: (typeof content.data)[0]): PiiJsonValue | null | undefined => {
    // First check if we decrypted this field (check both versioned and unversioned keys)
    const decryptedValue = decryptedData[field.dataIdentifier];
    if (decryptedValue !== undefined) {
      return decryptedValue;
    }
    // Also check unversioned key (API may return without version suffix)
    const unversionedKey = field.dataIdentifier.split(':')[0];
    const unversionedValue = decryptedData[unversionedKey];
    if (unversionedValue !== undefined) {
      return unversionedValue;
    }

    // Guard against malformed data - if field.field is missing, show "-"
    if (!field.field) {
      return undefined;
    }

    // Then check the backend field state
    if (field.field.kind === 'decrypted') {
      return field.field.value;
    }

    // If encrypted_vault without dataIdentifier, no data was collected → show "-"
    if (field.field.kind === 'encrypted_vault' && !field.field.dataIdentifier) {
      return undefined; // Will show "-" via isVaultDataEmpty
    }

    // If encrypted_vault with dataIdentifier, show encrypted placeholder → show bullets
    // Return null to trigger isVaultDataEncrypted check in FieldOrPlaceholder
    return null;
  };

  // Check if any fields can be decrypted:
  // - Must be encrypted_vault kind
  // - Must have dataIdentifier (data exists)
  // - Must not already be decrypted
  const hasDecryptableFields = content.data.some(field_0 => {
    if (field_0.field?.kind !== 'encrypted_vault') return false;
    if (!field_0.field.dataIdentifier) return false; // No data to decrypt
    // Check if already decrypted
    const decryptedValue_0 = decryptedData[field_0.dataIdentifier];
    const unversionedKey_0 = field_0.dataIdentifier.split(':')[0];
    const unversionedValue_0 = decryptedData[unversionedKey_0];
    return decryptedValue_0 === undefined && unversionedValue_0 === undefined;
  });
  const handleDecrypt = async () => {
    if (!onDecrypt) return;

    // Only decrypt fields that are encrypted_vault with dataIdentifier and not already decrypted
    const fieldsToDecrypt = content.data.filter(field_1 => {
      if (field_1.field?.kind !== 'encrypted_vault') return false;
      if (!field_1.field.dataIdentifier) return false;
      // Check if already decrypted
      const decryptedValue_1 = decryptedData[field_1.dataIdentifier];
      const unversionedKey_1 = field_1.dataIdentifier.split(':')[0];
      const unversionedValue_1 = decryptedData[unversionedKey_1];
      return decryptedValue_1 === undefined && unversionedValue_1 === undefined;
    }).map(field_2 => field_2.dataIdentifier);
    if (fieldsToDecrypt.length === 0) return;
    setIsDecrypting(true);
    try {
      const result = await onDecrypt(fieldsToDecrypt);
      if (result) {
        addDecryptedData(documentIdentifier, result);
      }
    } finally {
      setIsDecrypting(false);
    }
  };
  return <div className="flex flex-col gap-2 w-full">
      {content.data.map(field_3 => {
      const displayValue = getFieldDisplayValue(field_3);
      return <div key={field_3.id} className="flex justify-between gap-6">
            <p className="text-caption-2 text-tertiary">{field_3.displayName}</p>
            <FieldOrPlaceholder data={displayValue} />
          </div>;
    })}
      <AnimatePresence>
        {hasDecryptableFields && onDecrypt && <motion.div className="flex items-center justify-center pt-2 border-t border-tertiary mt-2" initial={false} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }}>
            {isDecrypting ? <LoadingSpinner /> : <LinkButton onClick={handleDecrypt}>{t($_0 => $_0.encryptedKeyValue.decrypt)}</LinkButton>}
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export default EncryptedKeyValueContent;